import { ModalProvider } from '@common/components/Modal';
import * as Sentry from '@sentry/nextjs';
import cookieParser from 'cookie';
import 'flag-icons/css/flag-icons.min.css';
import areIntlLocalesSupported from 'intl-locales-supported';
import moment from 'moment-timezone';
import NextSeo from 'next-seo';
import getConfig from 'next/config';
import Error from 'next/error';
import { useEffect, useMemo, useRef } from 'react';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { Toaster } from 'react-hot-toast';
import { resetId } from 'react-id-generator';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { BaseCSS } from 'styled-bootstrap-grid';
import { ImageTransformProvider } from 'voyager';

import { CheckoutLayoutVersion } from '@common/components/CheckoutLayoutVersion';
import { GeneralLayoutVersion } from '@common/components/GeneralLayout';
import { Routes } from '@common/constants/routes';
import AppWrapper from '@common/containers/AppWrapper';
import ModalError from '@common/containers/ModalError';
import { AppProvider } from '@common/contexts/AppContext';
import { AnalyticsScripts } from '@features/analytics';
import { getModuleStatuses } from '@features/properties';
import { QueryClient } from '@features/state';
import { translations } from '@lang';
import * as basketActions from '@store/basket/actions';
import * as entityActions from '@store/entity/actions';
import * as guestActions from '@store/guest/actions';
import configureStore from '@store/store';
import { QueryClient as ReactQueryClient, dehydrate } from '@tanstack/react-query';
import * as auth from '@utils/auth';
import config from '@utils/config';
import getAddressFromHost from '@utils/getAddressFromHost';
import request from '@utils/request';
import { dataLayerPush } from '@utils/tracking';
import setJourneyParams from '@utils/yourJourney';
import { toSnakeCase } from 'case-converter';
import { Router } from 'shared-routing';

import { propertiesKeys } from '@features/properties';
import { getRoomSettings, roomSettingsKeys } from '@features/roomSettings';
import { combineDehydratedStates } from '@features/state/combineDehydratedStates';
import { FeatureFlagProvider } from '@utils/featureFlagProvider';
import getHeadersForServerSideRequest from '@utils/getHeadersForServerSideRequest';
import GrowthBookAnalyticsScript from '@utils/GrowthBookAnalyticsScript';
import { imgixImageTransform } from '@utils/imgixImageTransform';
import defaultSeoConfig from '../../default-seo.config';

const { GOOGLE_TAG_MANAGER_ID, FEATURE_FLAGS_CLIENT_KEY } = getConfig().publicRuntimeConfig;

const localesMyAppSupports = ['en', 'fr', 'de', 'es', 'ru', 'pl'];

// React intl polyfill
if (global.Intl) {
  // Determine if the built-in `Intl` has the locale data we need.
  if (!areIntlLocalesSupported(localesMyAppSupports)) {
    // `Intl` exists, but it doesn't have the data we need, so load the
    // polyfill and patch the constructors we need with the polyfill's.
    /* tslint-disable-next-line global-require */
    const IntlPolyfill = require('intl'); // eslint-disable-line
    Intl.NumberFormat = IntlPolyfill.NumberFormat;
    Intl.DateTimeFormat = IntlPolyfill.DateTimeFormat;
  }
} else {
  // No `Intl`, so use and load the polyfill.
  /* eslint-disable-next-line global-require */
  global.Intl = require('intl');
}

// Standalone helper (was static method on class)
async function getModules(client, property) {
  const { data: moduleStatuses } = await client.fetchQuery({
    queryKey: propertiesKeys.moduleStatuses({ siteSlug: property.slug }),
    queryFn: () => getModuleStatuses({ siteSlug: property.slug }),
    cacheTime: Infinity,
    staleTime: Infinity,
  });
  return moduleStatuses;
}

// Memoized client-side store — replaces next-redux-wrapper's store management.
// On the server, a new store is created per request inside getInitialProps.
// On the client, the store is created once with the server's initial state and reused.
let sharedClientStore = null;

function getOrCreateStore(initialState) {
  if (typeof window === 'undefined') {
    // Server: always create a fresh store
    return configureStore(initialState);
  }
  // Client: create once, reuse thereafter
  if (!sharedClientStore) {
    sharedClientStore = configureStore(initialState);
  }
  return sharedClientStore;
}

function Storefront({
  Component,
  pageProps,
  errorCode,
  locale,
  messages,
  language,
  property,
  dehydratedState,
  initialReduxState,
}) {
  // Create or reuse the Redux store with server-side initial state
  const store = useMemo(() => getOrCreateStore(initialReduxState), [initialReduxState]);

  const routerListenerSetup = useRef(false);

  // Constructor-equivalent: set moment defaults + register router event
  useEffect(() => {
    if (property) {
      moment.tz.setDefault(property.timezone);
    }
    if (language && moment.locale() !== language) {
      moment.locale(language);
    }
  }, [property, language]);

  useEffect(() => {
    if (routerListenerSetup.current) return;
    routerListenerSetup.current = true;

    const handleRouteChange = (event) => {
      if ('gtag' in window) {
        gtag('event', 'page_view', {
          page_title: document.title,
          page_location: event,
        });
      }

      dataLayerPush({
        event: 'pageView',
        data: {
          url: event,
        },
      });
      window.scrollTo(0, 0);
    };

    Router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, []);

  // componentDidMount equivalent
  useEffect(() => {
    async function onMount() {
      if (errorCode) {
        return;
      }

      config.setLang(language);

      // date fns have not consistent naming convention for locales
      const languageToSet = language === 'en' ? 'en-GB' : language;

      if (languageToSet) {
        const fnsLocale = await import(`date-fns/locale/${languageToSet}/index`);
        registerLocale(languageToSet, fnsLocale);
        setDefaultLocale(languageToSet);
      }

      /* eslint-disable global-require */
      require('lazysizes');
      require('lazysizes/plugins/parent-fit/ls.parent-fit');
      require('lazysizes/plugins/attrchange/ls.attrchange');
      /* eslint-enable global-require */
    }

    onMount();
  }, []);

  function renderPageContent() {
    resetId();

    return (
      <ModalProvider>
        <Component {...pageProps} />
        <ModalError />
        <BaseCSS />
      </ModalProvider>
    );
  }

  if (errorCode) {
    return <Error statusCode={errorCode} />;
  }

  return (
    <Provider store={store}>
      <IntlProvider
        locale={language === 'en' ? 'en-GB' : language}
        messages={messages}
        key={language}
        timeZone={property.timezone}
      >
        <AppProvider value={{ siteId: property.id, siteSlug: property.slug }}>
          <FeatureFlagProvider clientKey={FEATURE_FLAGS_CLIENT_KEY} siteSlug={property.slug}>
            <GrowthBookAnalyticsScript slug={property.slug} />
            <ImageTransformProvider transform={imgixImageTransform}>
              <QueryClient dehydratedState={dehydratedState}>
                <NextSeo
                  config={defaultSeoConfig({
                    ...property,
                    ...pageProps,
                    locale,
                  })}
                />
                <AppWrapper language={language} property={property}>
                  {Component.isCheckout ? (
                    <CheckoutLayoutVersion>{renderPageContent()}</CheckoutLayoutVersion>
                  ) : (
                    <GeneralLayoutVersion>{renderPageContent()}</GeneralLayoutVersion>
                  )}
                </AppWrapper>
                <Toaster position="bottom-right" />
                <AnalyticsScripts
                  googleAnalyticsIds={[property.gaid, property.googleAnalyticsId]}
                  googleTagManagerIds={[GOOGLE_TAG_MANAGER_ID, property.googleTagManager]}
                />
              </QueryClient>
            </ImageTransformProvider>
          </FeatureFlagProvider>
        </AppProvider>
      </IntlProvider>
    </Provider>
  );
}

Storefront.getInitialProps = async ({ Component, ctx }) => {
  const { req, res } = ctx;
  const isServer = !!req;
  let errorCode;
  let serverData;
  let defaultPropertyPartySize;
  let tableDefaultPartySize;

  // Create a store for this request (server) or reuse the client-side store
  const store = getOrCreateStore();
  // Attach store to context so page-level getInitialProps can access it
  ctx.store = store;

  const client = new ReactQueryClient();

  if (isServer) {
    const {
      req: {
        headers: { host, cookie, 'user-agent': userAgent },
      },
    } = ctx;

    // Detect common bots/crawlers
    const isBot = /bot|crawler|spider|slurp|semrush|ahref/i.test(userAgent || '');

    // Tag bot traffic for filtering in Sentry dashboard
    Sentry.setTag('is_bot', isBot ? 'true' : 'false');

    const address = getAddressFromHost(host);
    const { language = '', basketId, token } = cookieParser.parse(cookie ?? '');

    const property = await request
      .disableUnauthorisedRedirect()
      .get(`properties/${address}${language ? `/${language}` : ''}?fallback=1`)
      .then(({ data }) => data)
      .catch((err) => {
        // Only capture unexpected errors (not 404s for unknown slugs)
        if (err.response?.status !== 404) {
          Sentry.captureException(err);
        } else {
          // Log 404s as breadcrumbs for debugging without creating noise
          Sentry.addBreadcrumb({
            category: 'property',
            message: `Property not found for slug: ${address}`,
            level: 'warning',
            data: { host, address },
          });
        }
      });

    if (!property) {
      res.statusCode = 404;
      return {
        errorCode: 404,
      };
    }

    Sentry.setTag('site_slug', property.slug);

    client.setQueryData(propertiesKeys.property(property.id), toSnakeCase(property));

    const moduleStatuses = await getModules(client, property);

    const requests = [];

    if (moduleStatuses.roomsEnabled) {
      requests.push(
        client
          .fetchQuery({
            queryKey: roomSettingsKeys.roomSettings({ siteSlug: property.slug }),
            queryFn: () => getRoomSettings({ siteSlug: property.slug }),
          })
          .then((response) => {
            defaultPropertyPartySize = response.data.defaultParty;
          })
          .catch(() => {
            // `/settings/rooms` responds with a 422 if a property doesn't have
            // the rooms module enabled so we want to let this error pass silently
          })
      );
    }

    if (property.modulesExcludingDisabled?.tables?.enabled) {
      requests.push(
        request.v2
          .get(`/store/pages/site/${property.slug}/settings/tables`)
          .then((response) => {
            const { adults: tableAdults = 2, children: tableChildren = 0 } =
              response.data.data.defaultParty;
            tableDefaultPartySize = { tableAdults, tableChildren };
          })
          .catch(() => {
            // `/settings/tables` responds with a 422 if a property doesn't have
            // the tables module enabled so we want to let this error pass silently
          })
      );
    }

    await Promise.all(requests);

    const languageToSet = language || property.defaultLanguage;
    const isTokenValid = auth.isTokenValid(token);

    if (isTokenValid) {
      const headers = getHeadersForServerSideRequest(req);

      await store.dispatch(guestActions.getUser(property.id, headers));
    }

    if (!isTokenValid && Component.isProtected) {
      res.writeHead(302, {
        Location: Routes.SIGNIN,
      });
    }

    const messages = translations[languageToSet];

    if (basketId) {
      await store.dispatch(
        basketActions.fetchBasketDetails(
          property.id,
          basketId,
          languageToSet,
          property.slug,
          property.timezone
        )
      );
    }

    const acceptLanguageHeader = ctx.req.headers['accept-language'];

    serverData = {
      messages,
      locale: acceptLanguageHeader ? acceptLanguageHeader.split(',')[0] : property.defaultLanguage,
      language: languageToSet,
      property,
      token: isTokenValid && token,
    };

    // setting existing property in redux store
    await store.dispatch(entityActions.fetchEntitySingleSuccess('properties', property));
    moment.tz.setDefault(property.timezone);
  } else {
    const token = auth.getToken();
    if (!auth.isTokenValid(token) && Component.isProtected) {
      Router.push(Routes.SIGNIN);
    }
  }

  if (errorCode) {
    return {
      errorCode,
    };
  }

  // Resolve app-level data: on the server, serverData is populated above.
  // On client-side navigations, serverData is undefined — read language/property
  // from the initial page data snapshot, then re-resolve messages from the static
  // translations import (window.__NEXT_DATA__ is a stale snapshot and won't have
  // updated messages for subsequent navigations).
  let messages, locale, language, property;
  if (serverData) {
    ({ messages, locale, language, property } = serverData);
  } else if (typeof window !== 'undefined') {
    // eslint-disable-next-line no-underscore-dangle
    const initialData = window.__NEXT_DATA__?.props || {};
    language = initialData.language;
    locale = initialData.locale;
    property = initialData.property;
    // Re-resolve messages from the static translations import — this is always
    // available on the client and doesn't go stale like the serialized snapshot.
    messages = language ? translations[language] : undefined;
  }

  if (property) {
    setJourneyParams(
      ctx,
      { ...defaultPropertyPartySize, ...tableDefaultPartySize },
      store,
      property
    );
  }

  const url = ctx.req ? `http://${ctx.req.headers.host}${ctx.asPath}` : null;

  let pageProps = {};

  try {
    if (property) {
      const moduleStatuses = await getModules(client, property);

      if (Component.getInitialProps) {
        pageProps = await Component.getInitialProps({
          ...ctx,
          store,
          moduleStatuses,
          property,
          language,
          url,
        });
      }
    } else if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps({
        ...ctx,
        store,
        url,
      });
    }
  } catch (err) {
    if (!err.response) {
      throw err;
    }
    errorCode = err.response.status;
  }

  return {
    pageProps,
    errorCode,
    messages,
    locale,
    language,
    property,
    query: ctx.query,
    dehydratedState: combineDehydratedStates(pageProps?.dehydratedState, dehydrate(client)),
    // Serialize the Redux store state so the client can hydrate with it
    initialReduxState: store.getState(),
  };
};

export default Storefront;
